import { Tokens } from '@/assets/tokens'
import { Button, Flex, SelectionOption, Selector } from '@/components'
import { DatePickerWrapper } from '@/components/core/DatePicker/DatePickerWrapper.component'
import { MultiSelector } from '@/components/core/Selector/MultiSelector/MultiSelector.component'
import { useRouting } from '@/hooks'
import { Alert, Api, Confirmation } from '@/modules'
import { ConfirmationContext } from '@/modules/confirmation/Confirmation.context'
import { Caution, ChartLineArea, Close, Download, Play, Plus } from '@icon-park/react'
import { useCallback, useContext, useState } from 'react'
import { ClassificationTestItem, ClassificationTestStage } from '../../types'
import {
  CloseButton,
  Container,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  FormField,
  FormLabel,
  FormRow,
  HeaderSection,
  HeaderTitle,
  ModalActions,
  ModalCard,
  ModalForm,
  ModalHeader,
  ModalSection,
  ModalSubtitle,
  ModalTitle,
  SectionTitle,
  StatusBadge,
  TestItem,
  TestItemDescription,
  TestItemHeader,
  TestItemInfo,
  TestItemMeta,
  TestItemsGrid,
  TestItemsSection,
  TestItemTitle,
} from './ClassificationTest.styles'
import { useClassificationTestPolling } from './useClassificationTestPolling'

interface Props {
  onRunTest: (testData: {
    startDate: string
    endDate: string
    dataSources: ('social' | 'press')[]
    stages: ClassificationTestStage[]
  }) => void
  activeBrandID: string | null
  isRunning: boolean
  testItems: ClassificationTestItem[]
}

const dataSourceOptions: SelectionOption[] = [
  { text: 'Redes Sociais', value: 'social' },
  { text: 'Imprensa', value: 'press' },
]

const stageOptions: SelectionOption[] = [
  { text: 'Temas', value: ClassificationTestStage.THEMES },
  { text: 'Abordagem', value: ClassificationTestStage.ROLE },
  { text: 'Polaridade + Intensidade', value: ClassificationTestStage.POLARITY_INTENSITY },
  { text: 'Mensagem-chave', value: ClassificationTestStage.KEY_MESSAGE },
]

interface ViabilityResponse {
  viable: boolean
  totalCount: number
  message?: string
}

export function ClassificationTest({ onRunTest, activeBrandID, isRunning, testItems }: Props): JSX.Element {
  const { companyID } = useRouting()
  const { sendAlert } = Alert.Hook()

  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [isCheckingViability, setIsCheckingViability] = useState(false)

  const confirmationContext = useContext(ConfirmationContext)

  const {
    binder: dataSourceBinder,
    selectedOptions: selectedDataSources,
    reset: resetDataSource,
  } = Selector.MultiBinder()

  const { binder: stagesBinder, selectedOptions: selectedStages, reset: resetStages } = Selector.MultiBinder()

  const [showModal, setShowModal] = useState(false)

  const {
    tests: localTestItems,
    addTest,
    hasProcessingTests,
    processingCount,
    forceCheckForUpdates,
  } = useClassificationTestPolling(testItems, {
    companyID,
    interval: 15000,
    pausePolling: isCheckingViability,
  })

  const [getDownloadUrl] = Api.PromiseQuery<{ url: string }>(
    `companies/:companyID/classification-tests/:testId/download`,
  )

  const [checkViability] = Api.PromiseQuery<ViabilityResponse>(
    `companies/:companyID/classification-tests/check-viability`,
  )

  const handleDownload = async (testId: string) => {
    try {
      const [response] = await getDownloadUrl({ companyID, testId })
      if (response?.url) {
        window.location.href = response.url
        sendAlert({
          type: 'success',
          title: 'Download iniciado',
          message: 'O arquivo será baixado em breve',
        })
      }
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro no download',
        message: 'Não foi possível baixar o resultado',
      })
      console.error('Erro ao baixar resultado:', error)
    }
  }

  const handleOpenModal = () => {
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setStartDate(null)
    setEndDate(null)
    resetDataSource()
    resetStages()
  }

  const executeTest = useCallback(
    async (dataSourcesToProcess: ('social' | 'press')[], startDate: Date, endDate: Date) => {
      const formatDateForDisplay = (date: Date) => {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
      }

      const formatDateForAPI = (date: Date) => {
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      const stagesToProcess = selectedStages.map((stage) => stage.value) as ClassificationTestStage[]

      for (const dataSource of dataSourcesToProcess) {
        const newTest: ClassificationTestItem = {
          id: `test-${Date.now()}-${dataSource}`,
          startDate: formatDateForDisplay(startDate),
          endDate: formatDateForDisplay(endDate),
          dataSource,
          status: 'PROCESSING',
          stages: stagesToProcess,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        addTest(newTest)
      }

      onRunTest({
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
        dataSources: dataSourcesToProcess,
        stages: stagesToProcess,
      })

      setShowModal(false)
      setStartDate(null)
      setEndDate(null)
      resetDataSource()
      resetStages()

      setTimeout(() => {
        forceCheckForUpdates()
      }, 1000)
    },
    [addTest, onRunTest, selectedStages, forceCheckForUpdates, resetDataSource, resetStages],
  )

  const handleRunTest = async () => {
    if (
      !startDate ||
      !endDate ||
      selectedDataSources.length === 0 ||
      selectedStages.length === 0 ||
      isCheckingViability
    )
      return

    const formatDateForAPI = (date: Date) => {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    setIsCheckingViability(true)

    try {
      const dataSources = selectedDataSources.map((ds) => ds.value).join(',')
      const stages = selectedStages.map((stage) => stage.value).join(',')
      const [viabilityResult] = await checkViability({
        companyID,
        brandID: activeBrandID || undefined,
        dataSources,
        stages,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
      })

      if (!viabilityResult?.viable) {
        sendAlert({
          type: 'error',
          title: 'Sem dados disponíveis',
          message: viabilityResult?.message || 'Não há dados para processar no período selecionado.',
        })
        return
      }

      const dataSourcesToProcess = selectedDataSources.map((ds) => ds.value) as ('social' | 'press')[]
      const message = `O teste será feito sobre ${viabilityResult.totalCount.toLocaleString('pt-BR')} postagens. Deseja executar agora?`
      confirmationContext.setData({
        icon: Caution,
        title: 'Confirmar execução do teste',
        text: message,
        confirmText: 'Sim! Executar',
        cancelText: 'Cancelar',
        onConfirm: () => {
          executeTest(dataSourcesToProcess, startDate, endDate)
        },
      })

      confirmationContext.open()
    } catch (error) {
      console.error('Erro ao verificar viabilidade:', error)
      sendAlert({
        type: 'error',
        title: 'Erro na verificação',
        message: 'Não foi possível verificar a viabilidade do teste. Tente novamente.',
      })
    } finally {
      setIsCheckingViability(false)
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PROCESSING':
        return 'Processando'
      case 'FINISHED':
        return 'Concluído'
      case 'ERROR':
        return 'Erro'
      case 'PENDING':
        return 'Pendente'
      default:
        return status
    }
  }

  const getDataSourceText = (dataSource: string) => {
    return dataSource === 'social' ? 'Redes Sociais' : 'Imprensa'
  }

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Container>
      <HeaderSection>
        <HeaderTitle>Testes de Classificação</HeaderTitle>
        <Button.Root $variant="primary" onClick={handleOpenModal} style={{ minWidth: '200px' }}>
          <Button.Icon value={Plus} color={Tokens.ColorWhite} />
          <Button.Text value={'Novo Teste'} />
        </Button.Root>
      </HeaderSection>

      <ModalSection $show={showModal}>
        <ModalCard>
          <ModalHeader>
            <div>
              <ModalTitle>Novo Teste de Classificação</ModalTitle>
              <ModalSubtitle>Configure o período e fonte de dados para executar o teste</ModalSubtitle>
            </div>
            <CloseButton onClick={handleCloseModal}>
              <Close size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalForm>
            <FormRow>
              <FormField>
                <FormLabel>Data Inicial *</FormLabel>
                <DatePickerWrapper
                  selected={startDate}
                  onChange={setStartDate}
                  placeholder="Selecione a data inicial"
                />
              </FormField>

              <FormField>
                <FormLabel>Data Final *</FormLabel>
                <DatePickerWrapper selected={endDate} onChange={setEndDate} placeholder="Selecione a data final" />
              </FormField>

              <FormField>
                <FormLabel>Fonte de Dados *</FormLabel>
                <MultiSelector
                  placeholder="Selecione as fontes de dados"
                  options={dataSourceOptions}
                  binder={dataSourceBinder}
                  allowExclusion={false}
                  $fit
                />
              </FormField>

              <FormField>
                <FormLabel>Etapas de Classificação *</FormLabel>
                <MultiSelector
                  placeholder="Selecione as etapas"
                  options={stageOptions}
                  binder={stagesBinder}
                  allowExclusion={false}
                  $fit
                />
              </FormField>
            </FormRow>

            <ModalActions>
              <Button.Root $variant="secondary" onClick={handleCloseModal}>
                <Button.Text value="Cancelar" />
              </Button.Root>
              <Button.Root
                $variant="primary"
                onClick={handleRunTest}
                disabled={
                  !startDate ||
                  !endDate ||
                  selectedDataSources.length === 0 ||
                  selectedStages.length === 0 ||
                  isRunning ||
                  isCheckingViability
                }
                $loading={isRunning || isCheckingViability}>
                <Button.Icon value={Play} color={Tokens.ColorWhite} />
                <Button.Text
                  value={
                    isCheckingViability ? 'Verificando...'
                    : isRunning ?
                      'Executando...'
                    : 'Executar Teste'
                  }
                />
              </Button.Root>
            </ModalActions>
          </ModalForm>
        </ModalCard>
      </ModalSection>

      <TestItemsSection>
        <SectionTitle>
          Histórico de Testes
          {hasProcessingTests && (
            <span
              style={{
                marginLeft: '12px',
                fontSize: '14px',
                color: '#ed8936',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}>
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#ed8936',
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite',
                }}
              />
              {processingCount} processando • Verificando a cada 15s
            </span>
          )}
        </SectionTitle>

        {localTestItems.length > 0 ?
          <TestItemsGrid>
            {localTestItems.map((item) => (
              <TestItem key={item.id}>
                <TestItemHeader>
                  <TestItemInfo>
                    <TestItemTitle>
                      {item.startDate} - {item.endDate}
                    </TestItemTitle>
                    <TestItemDescription>{getDataSourceText(item.dataSource)}</TestItemDescription>
                    <TestItemMeta>Executado em {formatDateTime(item.createdAt)}</TestItemMeta>
                    {item.errorMessage && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: Tokens.ColorUIDanger,
                          marginTop: '4px',
                          padding: '4px 8px',
                          backgroundColor: 'rgba(229, 62, 62, 0.1)',
                          borderRadius: '4px',
                        }}>
                        {item.errorMessage}
                      </div>
                    )}
                  </TestItemInfo>

                  <Flex $type="row" $align="center" $gap={8}>
                    {item.status === 'FINISHED' && (
                      <Button.Root
                        $variant="link"
                        onClick={() => handleDownload(item.id)}
                        title="Baixar resultado"
                        style={{ minWidth: 'auto', padding: '8px' }}>
                        <Button.Icon value={Download} color={Tokens.ColorUIPrimary} />
                      </Button.Root>
                    )}
                    <StatusBadge $status={item.status}>{getStatusText(item.status)}</StatusBadge>
                  </Flex>
                </TestItemHeader>
              </TestItem>
            ))}
          </TestItemsGrid>
        : <EmptyState>
            <ChartLineArea size={48} color="#9ca3af" />
            <EmptyStateTitle>Nenhum teste executado ainda</EmptyStateTitle>
            <EmptyStateDescription>
              Clique em &quot;Novo Teste&quot; para criar seu primeiro teste de classificação. Os resultados aparecerão
              aqui após a execução.
            </EmptyStateDescription>
          </EmptyState>
        }
      </TestItemsSection>
      <Confirmation.Component />
    </Container>
  )
}
